import { lockScroll, unlockScroll } from '@theme/scroll-lock';

function initMobileMenuDrawer() {
    if (window.__mobileMenuDrawerInitialized) return;
    const drawer = document.getElementById('mobile-menu-drawer');
    if (!drawer) return;
    window.__mobileMenuDrawerInitialized = true;
  
    const body = document.body;
    if (drawer.parentNode !== document.body) {
      document.body.appendChild(drawer);
    }
    const backdrop = drawer.querySelector('[data-drawer-backdrop]');
    const closeBtn = drawer.querySelector('[data-drawer-close]');
    const mainView = drawer.querySelector('[data-main-view]');
    const submenuViews = drawer.querySelectorAll('[data-submenu-view]');
    const backBtn = drawer.querySelector('[data-submenu-back]');
    const backLabel = drawer.querySelector('[data-submenu-back-label]');
    const menuParents = drawer.querySelectorAll('[data-menu-parent]');
    const countrySelector = drawer.querySelector('[data-country-selector]');
    const inlineBackButtons = drawer.querySelectorAll('[data-submenu-back-inline]');
    const inlineCloseButtons = drawer.querySelectorAll('[data-drawer-close-inline]');

    const show = el => el && el.classList.remove('is-hidden');
    const hide = el => el && el.classList.add('is-hidden');

    function resetSubmenuBannerHeight(hero, scrollEl) {
      if (!hero) return;
      hero.style.removeProperty('--submenu-banner-height');
      if (scrollEl) scrollEl.scrollTop = 0;
    }

    function updateSubmenuBannerHeight(hero, scrollEl) {
      if (!hero || !scrollEl) return;

      const styles = getComputedStyle(hero);
      const maxHeight = parseFloat(styles.getPropertyValue('--submenu-banner-height-max')) || 246;
      const minHeight = parseFloat(styles.getPropertyValue('--submenu-banner-height-min')) || 160;
      const range = Math.max(maxHeight - minHeight, 1);
      const progress = Math.min(1, Math.max(0, scrollEl.scrollTop / range));
      const nextHeight = maxHeight - progress * range;

      hero.style.setProperty('--submenu-banner-height', `${nextHeight}px`);
    }

    function bindSubmenuBannerCollapse(view) {
      const hero = view?.querySelector('[data-submenu-hero]');
      const scrollEl = view?.querySelector('.mobile-menu-drawer__submenu-scroll');
      if (!hero || !scrollEl || scrollEl.dataset.bannerCollapseBound === 'true') return;

      scrollEl.dataset.bannerCollapseBound = 'true';
      let frame = 0;

      const onScroll = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          updateSubmenuBannerHeight(hero, scrollEl);
        });
      };

      scrollEl.addEventListener('scroll', onScroll, { passive: true });
      resetSubmenuBannerHeight(hero, scrollEl);
    }

    function resetSubmenuState() {
      submenuViews.forEach((view) => {
        view.classList.remove('is-visible');
        const hero = view.querySelector('[data-submenu-hero]');
        const scrollEl = view.querySelector('.mobile-menu-drawer__submenu-scroll');
        resetSubmenuBannerHeight(hero, scrollEl);
      });
      if (mainView) mainView.classList.remove('is-hidden');
      hide(backBtn);
      hide(backLabel);
      if (countrySelector) countrySelector.classList.remove('is-hidden');
      drawer.classList.remove('is-submenu-open', 'is-image-submenu-open');
    }

    window.openMobileMenuDrawer = () => {
      if (drawer.parentNode !== document.body) {
        document.body.appendChild(drawer);
      }
      lockScroll();
      body.classList.add('menu-drawer-open');
      drawer.classList.remove('is-hidden');
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
    };

    const openFromBurger = (event) => {
      const burger = event.target?.closest?.('.btn_menu_burger');
      if (!burger) return false;
      event.preventDefault();
      window.openMobileMenuDrawer();
      return true;
    };

    document.addEventListener('click', (event) => {
      openFromBurger(event);
    });
  
    function closeDrawer() {
      drawer.classList.remove('is-open');
      setTimeout(() => {
        drawer.classList.add('is-hidden');
        body.classList.remove('menu-drawer-open');
        unlockScroll();
        resetSubmenuState();
      }, 300);
    }
  
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);
    window.closeMobileMenuDrawer = closeDrawer;
  
    if (backBtn) backBtn.addEventListener('click', resetSubmenuState);
    inlineBackButtons.forEach((button) => button.addEventListener('click', resetSubmenuState));
    inlineCloseButtons.forEach((button) => button.addEventListener('click', closeDrawer));

    menuParents.forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.menuId;
        const view = drawer.querySelector(`[data-submenu-view][data-menu-id="${id}"]`);
        const hasImage = view?.querySelector('[data-submenu-hero]') != null;
        submenuViews.forEach((v) => {
          v.classList.remove('is-visible');
          const hero = v.querySelector('[data-submenu-hero]');
          const scrollEl = v.querySelector('.mobile-menu-drawer__submenu-scroll');
          resetSubmenuBannerHeight(hero, scrollEl);
        });
        if (mainView) mainView.classList.add('is-hidden');
        view?.classList.add('is-visible');
        if (countrySelector) countrySelector.classList.add('is-hidden');
        drawer.classList.add('is-submenu-open');
        if (hasImage) {
          hide(backBtn);
          hide(backLabel);
          drawer.classList.add('is-image-submenu-open');
        } else {
          hide(backBtn);
          hide(backLabel);
          drawer.classList.remove('is-image-submenu-open');
        }
        if (view) {
          bindSubmenuBannerCollapse(view);
        }
        loadSubmenuCollectionsGrid(view);
      });
    });

    submenuViews.forEach((view) => bindSubmenuBannerCollapse(view));

    const submenuCollectionAbortControllers = new WeakMap();

    function getDrawerLocaleRoot() {
      const themePrefix = (window.theme?.pathPrefix || '').replace(/\/$/, '');
      if (themePrefix) return themePrefix;

      const pathname = window.location.pathname || '/';
      const firstSegment = pathname.split('/').filter(Boolean)[0] || '';
      if (/^(ar|ar-[a-z]{2}|en-[a-z]{2})$/i.test(firstSegment)) {
        return `/${firstSegment}`;
      }

      if (window.theme?.isRTL || document.documentElement.lang?.toLowerCase().startsWith('ar')) {
        return '/ar';
      }

      return '';
    }

    function getSubmenuCollectionsFetchUrl(handle) {
      const localeRoot = getDrawerLocaleRoot();
      const base = localeRoot ? `${localeRoot}/` : '/';
      return `${base}collections/${encodeURIComponent(handle)}?sections=mobile-menu-filter-cards`;
    }

    function loadSubmenuCollectionsGrid(view) {
      if (!view) return;

      const host = view.querySelector('[data-submenu-collections-host]');
      if (!host || host.dataset.collectionsLoaded === 'true') return;

      const handle = view.dataset.parentCollectionHandle || host.dataset.parentCollectionHandle;
      if (!handle) return;

      const controller = submenuCollectionAbortControllers.get(host);
      if (controller) controller.abort();
      const abortController = new AbortController();
      submenuCollectionAbortControllers.set(host, abortController);

      host.dataset.collectionsLoaded = 'loading';

      fetch(getSubmenuCollectionsFetchUrl(handle), {
        signal: abortController.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error('Failed to load submenu collections');
          return response.json();
        })
        .then((data) => {
          if (abortController.signal.aborted) return;

          const html = data['mobile-menu-filter-cards'];
          if (!html) return;

          const doc = new DOMParser().parseFromString(html.trim(), 'text/html');
          const fetchedHost = doc.querySelector('[data-submenu-collections-host]');
          const fetchedBlock = doc.querySelector('.mobile-menu-drawer__submenu-collections');

          host.replaceChildren();
          if (fetchedBlock) {
            host.appendChild(fetchedBlock.cloneNode(true));
          } else if (fetchedHost) {
            fetchedHost.childNodes.forEach((node) => {
              host.appendChild(node.cloneNode(true));
            });
          }

          host.dataset.collectionsLoaded = 'true';
        })
        .catch((error) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          host.dataset.collectionsLoaded = 'false';
        });
    }
  }

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileMenuDrawer, { once: true });
} else {
  initMobileMenuDrawer();
}

document.addEventListener('shopify:section:load', initMobileMenuDrawer);
